import { toRelativeUrl } from '@okta/okta-auth-js'
import { Security, useOktaAuth } from '@okta/okta-react'
import { useUnleashClient, useUnleashContext } from '@unleash/proxy-client-react'
import { useEffect, type PropsWithChildren } from 'react'
import { useNavigate } from 'react-router'
import { useUserStore, type UserInfo } from '~/entities/user'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { oktaAuth } from '~/shared/config/auth'
import { addUserProperties, identifyUser } from '~/shared/lib/analytics'

export function AuthSecurity({ children }: PropsWithChildren) {
  const navigate = useNavigate()

  return (
    <Security
      oktaAuth={oktaAuth}
      restoreOriginalUri={(_, url) => navigate(url || '/', { replace: true })}
    >
      {children}
    </Security>
  )
}

export const SecureRoute = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const { authState, oktaAuth: oktaAuthFromContext } = useOktaAuth()
  const { userInfo, setUserInfo } = useUserStore()
  const unleashClient = useUnleashClient()
  const updateUnleashContext = useUnleashContext()

  useEffect(() => {
    if (!authState) {
      return
    }

    if (!authState.isAuthenticated) {
      setUserInfo(null)
      const originalUri = toRelativeUrl(window.location.href, window.location.origin)
      oktaAuthFromContext.setOriginalUri(originalUri)
      navigate('/login')
      return
    }

    apiClient.client.setConfig({
      baseUrl: import.meta.env.VITE_API_BASE_SECURE_URL,
      headers: {
        Authorization: `Bearer ${authState.accessToken?.accessToken}`,
      },
    })

    const fetchUserInfo = async () => {
      try {
        const userInfo = await oktaAuthFromContext.token.getUserInfo()
        setUserInfo(userInfo as UserInfo)
      } catch {
        oktaAuthFromContext.signInWithRedirect()
      }
    }
    if (!userInfo) {
      fetchUserInfo()
    }
  }, [authState, oktaAuthFromContext, navigate, setUserInfo, userInfo])

  useEffect(() => {
    if (userInfo?.email) {
      try {
        if (userInfo.email) {
          identifyUser(userInfo.email as string)
        }
        addUserProperties({
          email: userInfo.email,
          name: userInfo.name,
        })
      } catch (error) {
        console.error('Error identifying user', error)
      }
    }
  }, [userInfo?.email, userInfo?.name])

  useEffect(() => {
    if (userInfo?.email) {
      try {
        updateUnleashContext({
          ...unleashClient.getContext(),
          userId: userInfo.email as string,
        })
      } catch (error) {
        console.error('Error starting Unleash', error)
      }
    }
  }, [userInfo?.email, updateUnleashContext, unleashClient])

  if (!authState || !authState.isAuthenticated) {
    return null
  }

  return children
}
